import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { OccurrenceResponse } from '@kidshome/api-contract';
import { MissionsBoard } from '@/widgets/missions/MissionsBoard';

/** How many featured mission tiles the Home digest shows before "See all" — ONE clean row (the grid is
 *  at most 3 columns), never a lone wrapped 4th tile. */
const FEATURED_COUNT = 3;

/**
 * The Home Missions DIGEST (ADR-0141) — a compact taste of the backlog so the day Home stays calm no
 * matter how many missions pile up. Shows a few FEATURED cover tiles (doable inline) + a "See all →"
 * into the full `/missions` shelf. This is the fix for "the Missions lane itself becomes a wall": the
 * full list lives on its own screen; Home only ever shows a handful.
 */
export function MissionsDigest({
  missions,
  assigneeId,
  canComplete,
}: {
  missions: readonly OccurrenceResponse[];
  assigneeId: string;
  canComplete: boolean;
}): React.ReactElement {
  const { t } = useTranslation('family');
  // The LAST few missions (the tail of the shelf's order) — a small taste; the full list is one tap away.
  const featured = missions.slice(-FEATURED_COUNT);
  const hasMore = missions.length > featured.length;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="bucket-h">{t('missions.digestTitle')}</h2>
        <Link to="/missions" className="text-sm font-bold text-primary hover:underline">
          {hasMore ? t('missions.seeAllCount', { count: missions.length }) : t('missions.seeAll')}
        </Link>
      </div>
      <p className="missions-lead">{t('missions.lead')}</p>
      {/* Keep the digest to ONE row on every device: the grid is 2 cols on a phone and 3 on wider screens,
          so the 3rd featured tile is hidden below `sm` (mobile shows 2, desktop shows 3) — never a lone
          wrapped tile. The full list (any count) is one tap away via "See all". */}
      <MissionsBoard
        missions={featured}
        assigneeId={assigneeId}
        canComplete={canComplete}
        className="[&>*:nth-child(3)]:hidden sm:[&>*:nth-child(3)]:block"
      />
    </section>
  );
}
